import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {VectorFillOptions} from '@Panels/poster-editor-vector-fill-panel/components/vector-fill-options';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import type {RGBA} from '@Utils/color.util';
import {convertFillObjectToFabricColor, convertFabricColorToFillObject} from '@Utils/color.util';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';

interface PosterEditorVectorFillPanelProps {
  panelId: string;
}

enum VectorFillPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export default function PosterEditorVectorFillPanel({...props}: PosterEditorVectorFillPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [panelToShow, setPanelToShow] = useState(VectorFillPanels.DEFAULT);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const fill = useGetKeyFromActiveItem('fill');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  if (!activeItem || fill === undefined) {
    return null;
  }

  const fillObject = useMemo(() => {
    return convertFabricColorToFillObject(fill);
  }, [fill]);

  if (!activeItem.isVector() && !activeItem.isRectangle()) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_fill')} />;
  };

  const getContent = (): ReactElement => {
    return (
      <VectorFillOptions
        panelId={props.panelId}
        onClickColorFill={(index): void => {
          setSelectedColorIndex(index);
          setPanelToShow(VectorFillPanels.COLOR_PICKER);
        }}
      />
    );
  };

  const getDefaultPanel = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  const onBackCallback = (): void => {
    setPanelToShow(VectorFillPanels.DEFAULT);
  };

  const updateFillColor = (fillColor: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        fill: convertFillObjectToFabricColor({
          fillType: fillObject.fillType,
          fillColor,
        }),
      },
      {undoable}
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        onBackButton={onBackCallback}
        panelId={props.panelId}
        fillObject={fillObject}
        initialSelectedColorIndex={selectedColorIndex}
        onChangeColor={updateFillColor}
        onClose={onClose}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case VectorFillPanels.COLOR_PICKER:
        return getColorPanel();
      case VectorFillPanels.DEFAULT:
      default:
        return getDefaultPanel();
    }
  };

  return getPanels();
}
